import { Component, effect, inject, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { BookmarkCategoryStore } from '../../../stores/bookmark-category/bookmark-category.state';
import { Bookmark, LayoutOptions } from '../../../models/bookmark';
import { FormFieldWrapperComponent } from '../../molecules/form-field-wrapper/form-field-wrapper.component';
import { TagDropdownComponent } from '../../atoms/tag-dropdown/tag-dropdown.component';
import { DividerModule } from 'primeng/divider';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePipe, NgIf } from '@angular/common';
import { AppUtilityService, isExpired } from '../../../utility/app.utility';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { BaseFormComponent } from '../../molecules/base-form/base-form.component';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { CardModule } from 'primeng/card';
import { getState } from '@ngrx/signals';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { PermissionService } from '../../../services/permission.service';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { FormFooterComponent } from '../../molecules/form-footer/form-footer.component';
import { CustomGridStackWidget } from '../../../services/grid-widget-config.service';
import { Button } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { StyleClassModule } from 'primeng/styleclass';
import { GlobalsStore } from '../../../stores/globals.state';
import { CalendarModule } from 'primeng/calendar';
import { TooltipModule } from 'primeng/tooltip';
import { GuestLinkService } from '../../../services/bookmark/guest-link.service';
import { PanelModule } from 'primeng/panel';
import { InputOtpModule } from 'primeng/inputotp';
import { PrimeNGConfig } from 'primeng/api';
import { BookmarkService } from '../../../services/bookmark/bookmark.service';
import { BookmarkCategoryHelperService } from '../../../services/bookmark-category/bookmark-category-helper.service';

@Component({
  selector: 'app-bookmark-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormFieldWrapperComponent,
    TagDropdownComponent,
    DividerModule,
    InputTextModule,
    InputSwitchModule,
    MultiSelectModule,
    NgIf,
    CardModule,
    AlertComponent,
    FormFooterComponent,
    Button,
    DialogModule,
    StyleClassModule,
    CalendarModule,
    DatePipe,
    TooltipModule,
    PanelModule,
    InputOtpModule,
  ],
  templateUrl: './bookmark-form.component.html',
  styleUrl: './bookmark-form.component.scss',
})
export class BookmarkFormComponent
  extends BaseFormComponent<Bookmark>
  implements OnInit
{
  readonly store = inject(BookmarkStore);
  readonly globalsStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly bookmarkCategoryStore = inject(BookmarkCategoryStore);
  readonly bookmarkCategoryHelper = inject(BookmarkCategoryHelperService);
  initialized = false;
  // Holds the multiselect's chosen category ids for a not-yet-persisted
  // bookmark until its real id comes back from the server - see `onceCreated` below.
  private pendingCategoryAssignment: string[] | null = null;
  guestLinkExpanded = false;
  pwMasked = false;
  now = new Date();
  override form: FormGroup = this.fb.group({});
  override itemToEdit?: Bookmark;
  @Input() public bookmark?: Bookmark;
  @Input() public layout: CustomGridStackWidget[] | undefined;
  @Input() public layoutOptions: LayoutOptions | null = null;
  @Input() public atlasForm = false;

  constructor(
    router: Router,
    route: ActivatedRoute,
    appUtilityService: AppUtilityService,
    routingService: RoutingService,
    permissionService: PermissionService,
    confirmationDialogService: ConfirmationDialogService,
    private guestLinkService: GuestLinkService,
    private fb: FormBuilder,
    private primengConfig: PrimeNGConfig,
    private bookmarkService: BookmarkService
  ) {
    super(
      router,
      route,
      appUtilityService,
      routingService,
      permissionService,
      confirmationDialogService
    );

    effect(() => {
      const bookmarkState = getState(this.store);
      const categoryState = getState(this.bookmarkCategoryStore);
      if (bookmarkState.selectedId) {
        this.initializeForm();
        this.initialized = true;
        this.pwMasked = !!this.itemToEdit?.guestLink?.password;
      }

      if (categoryState.selectedId && this.initialized) {
        setTimeout(() => {
          const current: string[] = this.form.get('bookmarkCategories')
            ?.value || [];
          if (!current.includes(categoryState.selectedId as string)) {
            this.form.patchValue({
              bookmarkCategories: [...current, categoryState.selectedId],
            });
          }
        });
      }
    });

    // A newly created bookmark only gets its _id once the server responds,
    // so the categories picked in the multiselect have to wait for that
    // before they can be written onto the chosen BookmarkCategory documents.
    this.onceCreated(
      () => this.pendingCategoryAssignment !== null,
      id => {
        const selected = this.pendingCategoryAssignment || [];
        this.pendingCategoryAssignment = null;
        this.applyCategoryAssignments(id, [], selected);
      }
    );
  }

  /**
   * Lifecycle hook that is called after Angular has initialized the component.
   * This method checks for access permissions, initializes the form if it exists,
   * and updates the initialization status.
   *
   * @return {void} Does not return any value.
   */
  override ngOnInit(): void {
    this.primengConfig.setTranslation({
      firstDayOfWeek: 1,
      dayNames: [
        'Sonntag',
        'Montag',
        'Dienstag',
        'Mittwoch',
        'Donnerstag',
        'Freitag',
        'Samstag',
      ],
      dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
      monthNames: [
        'Januar',
        'Februar',
        'März',
        'April',
        'Mai',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'Dezember',
      ],
      monthNamesShort: [
        'Jan',
        'Feb',
        'Mär',
        'Apr',
        'Mai',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dez',
      ],
      today: 'Heute',
      clear: 'Löschen',
    });
    this.hasAccess();
    if (this.atlasForm) {
      this.initializeForm();
      this.initialized = true;
    }
  }

  /**
   * Initializes the form with default or pre-existing values based on the currently selected item or bookmark.
   * It creates form controls for various properties such as title, headline, category, and others.
   *
   * @return {void} Does not return a value.
   */
  initializeForm(): void {
    this.itemToEdit = this.bookmark || this.store.selectedItem();

    // Check if guest link exists and expand section
    if (this.itemToEdit?.guestLink?.enabled) {
      this.guestLinkExpanded = true;
    }

    const isPrivate = this.itemToEdit?.private || false;
    const isPublished = this.itemToEdit?.published || false;

    this.form = this.fb.group({
      title: [this.itemToEdit?.title || '', Validators.required],
      headline: [this.itemToEdit?.headline || ''],
      bookmarkCategories: [
        this.itemToEdit?._id
          ? this.bookmarkCategoryHelper.getCategoriesForBookmark(
              this.itemToEdit._id
            )
          : [],
      ],
      published: [
        {
          value: isPublished,
          disabled: isPrivate,
        },
      ],
      useAsTemplate: [this.itemToEdit?.useAsTemplate || false],
      tags: [this.itemToEdit?.tags || []],
      config: [this.itemToEdit?.config || []],
      layoutOptions: [this.itemToEdit?.layoutOptions || null],
      disableControls: [this.itemToEdit?.disableControls || false],
      private: [
        {
          value: isPrivate,
          disabled: isPublished,
        },
      ],
      createdBy: [this.itemToEdit?.createdBy || this.activeUserStore.user()],
      guestLink: this.fb.group({
        enabled: [this.itemToEdit?.guestLink?.enabled || false],
        token: [this.itemToEdit?.guestLink?.token || ''],
        password: [this.itemToEdit?.guestLink?.password || ''],
        expiresAt: [
          this.itemToEdit?.guestLink?.expiresAt
            ? new Date(this.itemToEdit.guestLink.expiresAt)
            : null,
        ],
        createdAt: [this.itemToEdit?.guestLink?.createdAt || null],
        createdBy: [
          this.itemToEdit?.guestLink?.createdBy ||
            this.activeUserStore.user()?._id,
        ],
      }),
    });

    // Watch for private/published toggle to manage disabled states
    this.form.get('private')?.valueChanges.subscribe(isPrivate => {
      const publishedControl = this.form.get('published');
      if (isPrivate) {
        publishedControl?.disable({ emitEvent: false });
      } else {
        publishedControl?.enable({ emitEvent: false });
      }
    });

    this.form.get('published')?.valueChanges.subscribe(isPublished => {
      const privateControl = this.form.get('private');
      if (isPublished) {
        privateControl?.disable({ emitEvent: false });
      } else {
        privateControl?.enable({ emitEvent: false });
      }
    });

    // Watch for enabled toggle
    this.form.get('guestLink.enabled')?.valueChanges.subscribe(enabled => {
      if (enabled && !this.form.get('guestLink.token')?.value) {
        this.generateGuestToken();
      }
    });
  }

  /**
   * Handles the submission process for a form. If a layout is defined, it updates the form's values
   * with the layout configuration before invoking the submit action.
   *
   * @return {void} Does not return a value.
   */
  onSubmit(): void {
    if (this.layout) {
      this.form.patchValue({ config: this.layout });
    }

    if (this.layoutOptions) {
      this.form.patchValue({ layoutOptions: this.layoutOptions });
    }

    // Clean up guest link if disabled
    const guestLinkEnabled = this.form.get('guestLink.enabled')?.value;
    if (!guestLinkEnabled) {
      this.form.patchValue({ guestLink: undefined });
    } else {
      // Set createdAt if this is the first time enabling
      const guestLink = this.form.get('guestLink')?.value;
      if (!guestLink.createdAt) {
        this.form.patchValue({
          guestLink: {
            ...guestLink,
            createdAt: new Date(),
          },
        });
      }
    }

    // `bookmarkCategories` only drives the multiselect locally - the
    // relationship itself now lives on `BookmarkCategory.bookmarks`, so it's
    // never part of the Bookmark payload itself and has to be stripped out
    // before submitting.
    const selectedCategoryIds: string[] =
      this.form.get('bookmarkCategories')?.value || [];

    if (this.itemToEdit && this.itemToEdit._id) {
      const previousCategoryIds =
        this.bookmarkCategoryHelper.getCategoriesForBookmark(
          this.itemToEdit._id
        );
      this.applyCategoryAssignments(
        this.itemToEdit._id,
        previousCategoryIds,
        selectedCategoryIds
      );
    } else {
      this.pendingCategoryAssignment = selectedCategoryIds;
    }

    if (!this.layout && this.itemToEdit && this.itemToEdit._id) {
      // todo - not the best hook - need sockets... then remove this
      this.bookmarkService
        .getItem(this.itemToEdit._id)
        .then((item: Bookmark) => {
          this.form.patchValue({ config: item.config });
          const { bookmarkCategories, ...valueWithoutCategories } =
            this.form.value;
          this.submit(valueWithoutCategories);
        });
    } else {
      const { bookmarkCategories, ...valueWithoutCategories } =
        this.form.value;
      this.submit(valueWithoutCategories);
    }
  }

  /**
   * Adds/removes the given bookmark's id to/from every affected
   * BookmarkCategory's `bookmarks` array so it ends up assigned to exactly
   * `selectedIds` - one update per category that actually changed, never a
   * blanket rewrite of the whole array (which would also clobber other
   * bookmarks' positions in it).
   *
   * @param {string} bookmarkId - The bookmark being (re)assigned.
   * @param {string[]} previousIds - The category ids it belonged to before this save.
   * @param {string[]} selectedIds - The category ids chosen in the multiselect.
   * @return {void} Does not return a value.
   */
  private applyCategoryAssignments(
    bookmarkId: string,
    previousIds: string[],
    selectedIds: string[]
  ): void {
    const added = selectedIds.filter(id => !previousIds.includes(id));
    const removed = previousIds.filter(id => !selectedIds.includes(id));

    added.forEach(categoryId => {
      const category = this.bookmarkCategoryStore.getById(categoryId);
      if (!category) return;
      this.bookmarkCategoryStore.update({
        ...category,
        bookmarks: [...(category.bookmarks || []), bookmarkId],
      });
    });

    removed.forEach(categoryId => {
      const category = this.bookmarkCategoryStore.getById(categoryId);
      if (!category) return;
      this.bookmarkCategoryStore.update({
        ...category,
        bookmarks: (category.bookmarks || []).filter(id => id !== bookmarkId),
      });
    });
  }

  /**
   * Opens a form dialog to add a new bookmark category.
   *
   * This method triggers an event named 'addBookmarkCategoryDialog' within the global store.
   * The event is used to display a dialog for creating a new bookmark category.
   *
   * @return {void} Does not return any value.
   */
  openBookmarkCategoryForm(): void {
    this.globalsStore.triggerAtlasEvent('addBookmarkCategoryDialog', {});
  }

  /**
   * Generates a new guest token
   */
  generateGuestToken(): void {
    const token = this.guestLinkService.generateToken();
    this.form.patchValue({
      guestLink: {
        token,
        createdAt: new Date(),
        createdBy: this.activeUserStore.user()?._id,
      },
    });
    if (this.form.get('guestLink.expiresAt')?.value) {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 1);
      this.form.patchValue({
        guestLink: {
          expiresAt,
        },
      });
    }
  }

  deleteGuestLink(): void {
    this.form.get('guestLink')?.reset();
  }

  /**
   * Gets the full guest link URL
   */
  getGuestLinkUrl(): string {
    const token = this.form.get('guestLink.token')?.value;
    return token ? this.guestLinkService.getGuestLinkUrl(token) : '';
  }

  /**
   * Copies the guest link to clipboard
   */
  async copyGuestLink(): Promise<void> {
    const url = this.getGuestLinkUrl();
    await this.guestLinkService.copyToClipboard(url);
  }

  /**
   * Opens guest link in new tab for testing
   */
  openGuestLink(): void {
    const url = this.getGuestLinkUrl();
    window.open(url, '_blank');
  }

  /**
   * Copies the guest link password to the clipboard.
   * The method retrieves the password value from the form and uses the guestLinkService
   * to copy it to the clipboard.
   *
   * @return {void} Does not return a value.
   */
  copyPassword() {
    this.guestLinkService.copyPasswordToClipboard(
      this.form.get('guestLink.password')?.value
    );
  }

  protected readonly isExpired = isExpired;
}
